import React from "react";

export const humanizeParameterKey = (key) => {
  const labelMap = {
    organism: "Organism",
    alignment_tool: "Alignment tool",
    phred_quality_score: "Phred quality score",
    minimum_read_length_required: "Minimum read length required",
    min_read_length: "Minimum read length",
    log2_fold_change: "Log2 fold change",
    threshold_type: "Threshold type",
    threshold_value: "Threshold value",
    significance_threshold_type: "Significance threshold type",
    significance_threshold_value: "Significance threshold value",
    motif_significance_threshold: "Motif significance threshold",
    target_bed_file_link: "Target BED file link",
    sample_metadata: "Sample Metadata",
    metadata_builder: "Sample Metadata",
    manifest_file_rows: "Manifest File",
    metadata_file_rows: "Metadata File",
    raw_data_qc: "Raw Data QC",
    differential_gene_expression: "Differential Gene Expression",
    annotation: "Annotation",
    metadata: "Metadata",
  };

  return labelMap[key] || key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatReviewValue = (value) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(formatReviewValue).join(", ");
  if (value && typeof value === "object") {
    if (value.renderName) return value.renderName;
    if (value.name) return value.name;
    return JSON.stringify(value);
  }
  return String(value);
};

export const buildParameterRows = (parameters = {}) => {
  const rows = [];
  const sectionBefore = {
    peak_type: "Peak Calling Parameters",
    motif_database: "Motif Enrichment Parameters",
  };

  Object.entries(parameters || {}).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined || key === "metadata_locked") return;

    if (sectionBefore[key]) rows.push({ label: sectionBefore[key], value: "__SECTION__" });

    if ((key === "metadata_builder" || key === "sample_metadata") && Array.isArray(value)) {
      rows.push({ label: "Sample Metadata", value });
      return;
    }

    if (key === "manifest_file_rows" && Array.isArray(value)) {
      rows.push({ label: "Manifest File", value });
      return;
    }

    if (key === "metadata_file_rows" && Array.isArray(value)) {
      rows.push({ label: "Metadata File", value });
      return;
    }

    if (["raw_data_qc", "differential_gene_expression", "annotation"].includes(key) && value && typeof value === "object") {
      rows.push({
        label: key === "annotation" ? "Annotation Parameters" : humanizeParameterKey(key),
        value: "__SECTION__",
      });
      Object.entries(value).forEach(([childKey, childValue]) => {
        if (childValue !== "" && childValue !== null && childValue !== undefined) {
          rows.push({ label: humanizeParameterKey(childKey), value: formatReviewValue(childValue) });
        }
      });
      return;
    }

    if (key === "comparisons" && value && typeof value === "object") {
      rows.push({ label: "Comparisons", value });
      return;
    }

    rows.push({ label: humanizeParameterKey(key), value: formatReviewValue(value) });
  });

  return rows;
};

function DataTable({ columns, rows, minWidth = "min-w-[300px]" }) {
  return (
    <table className={`${minWidth} border border-gray-200 rounded-md`}>
      <thead className="bg-gray-50">
        <tr>
          <th className="border px-3 py-2 text-left text-xs font-semibold">#</th>
          {columns.map(({ key, label }) => (
            <th key={key} className="border px-3 py-2 text-left text-xs font-semibold">{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            <td className="border px-3 py-2">{index + 1}</td>
            {columns.map(({ key }) => (
              <td key={key} className="border px-3 py-2 break-all">{formatReviewValue(row?.[key] ?? "—")}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ParameterValue({ row }) {
  if (row.label === "Sample Metadata" && Array.isArray(row.value)) {
    const columns = Array.from(new Set(row.value.flatMap((sample) => Object.keys(sample || {}))))
      .map((key) => ({ key, label: humanizeParameterKey(key) }));
    return <DataTable columns={columns} rows={row.value} />;
  }

  if (row.label === "Manifest File" && Array.isArray(row.value)) {
    return <DataTable minWidth="min-w-[650px]" rows={row.value} columns={[
      { key: "sample_id", label: "Sample ID" },
      { key: "forward_absolute_path", label: "Forward Absolute Path" },
      { key: "reverse_absolute_path", label: "Reverse Absolute Path" },
    ]} />;
  }

  if (row.label === "Metadata File" && Array.isArray(row.value)) {
    return <DataTable minWidth="min-w-[350px]" rows={row.value} columns={[
      { key: "id", label: "ID" },
      { key: "status", label: "Status" },
    ]} />;
  }

  if (row.label === "Comparisons") {
    return <DataTable rows={row.value?.items || []} columns={[
      { key: "test", label: "Test" },
      { key: "control", label: "Control" },
    ]} />;
  }

  return row.value;
}

export function ParameterReviewTable({ parameters, emptyMessage = "—" }) {
  const rows = buildParameterRows(parameters);

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Parameter</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {rows.length ? rows.map((row, index) => (
            row.value === "__SECTION__" ? (
              <tr key={`${row.label}-${index}`}>
                <td colSpan={2} className="bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">{row.label}</td>
              </tr>
            ) : (
              <tr key={`${row.label}-${index}`}>
                <td className="px-3 py-2 font-medium text-gray-700">{row.label}</td>
                <td className="px-3 py-2 text-gray-900"><ParameterValue row={row} /></td>
              </tr>
            )
          )) : (
            <tr><td colSpan="2" className="px-3 py-3 text-gray-500">{emptyMessage}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function RawDataSourceReview({ source, legacyUrls = [], showPassword = false }) {
  const rawDataSource = source || { type: "online", link: legacyUrls[0] || "" };
  const isOnline = rawDataSource.type !== "offline";

  return (
    <div className="space-y-1 text-sm text-gray-900">
      <div><strong>Type:</strong> {isOnline ? "Online" : "Offline"}</div>
      {isOnline ? (
        <>
          <div className="break-all"><strong>Download Link:</strong> {rawDataSource.link || "—"}</div>
          {rawDataSource.username && <div><strong>Username:</strong> {rawDataSource.username}</div>}
          {rawDataSource.password && <div><strong>Password:</strong> {showPassword ? rawDataSource.password : "********"}</div>}
        </>
      ) : <div>Offline data transfer selected.</div>}
    </div>
  );
}
